import React, { useState, useEffect, useContext } from 'react';
import _ from 'lodash';
import PropTypes from 'prop-types';
import { ViewModelForm, ViewModelServiceContext, useDataRefresh } from '@xengage/gw-portals-viewmodel-react';
import { useTranslator } from '@jutro/locale';
import VehicleDriversComponent from '../VehicleDriversComponent/VehicleDriversComponent';

import metadata from './VehiclesListComponent.metadata.json5';
import policychangeMetadata from './VehiclesListComponent.policychange.metadata.json5';
import messages from './VehiclesListComponent.messages';
import styles from './VehiclesListComponent.module.scss';

const TOTAL_PCT = 100;
const RESIDENT_STUDENT_CODE = 'PADriverResidentStudent_ML';

const VehiclesListComponent = (props) => {
    const {
        isVehiclePrimaryDriverState, isVehiclePrimaryDriverPercentageState, isPolicyChange, isGPA, drivers, vehicles, vehicleDrivers, vehicleDriverErrors, setVehicleDriverErrors
    } = props;
    const { refreshData } = useDataRefresh();

    const viewModelService = useContext(ViewModelServiceContext);
    const translator = useTranslator();

    const [errors, setErrors] = useState([]);

    const vehiclesEligibleList = _.filter(
        vehicles,
        (vehicle) => {
            return vehicle.category_ML != 'trailer';
        }
    );

    const isVehicleEligibleForAccidentPrevention = (vehiclesEligibleList) => {
        if (_.isNil(vehiclesEligibleList)) {
            return false;
        }
        return (vehiclesEligibleList.category_ML === 'private_passenger_light_truck')
            || (vehiclesEligibleList.category_ML === 'collectible' && (vehiclesEligibleList.type_ML === 'classic_ml' || vehiclesEligibleList.type_ML === 'replica_ml' || vehiclesEligibleList.type_ML === 'custom_ml'));
    };

    const getVehiclesForAssignment = () => {
        return vehiclesEligibleList.filter((vehicle) => isVehicleEligibleForAccidentPrevention(vehicle));
    };

    const hasResidentStudentModifier = (driver) => {
        const residentStudentModifier = driver.driverModifiers_ML.modifiers.find((mod) => mod.codeIdentifier_ML === RESIDENT_STUDENT_CODE);
        return residentStudentModifier && residentStudentModifier.chosenTermValue === 'true';
    };

    const everyDriverAssigned = (listOfDrivers, listOfVehicleDrivers) => {
        let resp = true;
        listOfDrivers.forEach((driver) => {
            if (listOfVehicleDrivers.find((vd) => vd.driverID === _.get(driver, 'fixedId')) === undefined) resp = false;
        });
        return resp;
    };

    const driversOnlyAssignedOnce = (listOfDrivers, listOfVehicleDrivers) => {
        let resp = true;
        if (listOfDrivers.every((driver) => listOfVehicleDrivers.filter((vd) => vd.driverID === _.get(driver, 'fixedId')).length > 1)) resp = false;
        return resp;
    };


    const getRatedDriversNotStudent = () => {
        return drivers.filter((driver) => {
            return !hasResidentStudentModifier(driver);
        });
    };

    const validateVehiclePrimaryDriverAssignments = () => {
        const eligibleVehicles = vehiclesEligibleList;
        const driversNotStudent = getRatedDriversNotStudent();
        return vehicleDrivers.length === eligibleVehicles.length
            && vehicleDrivers.value.every((vd) => vd.percentageDriven_Ext === TOTAL_PCT)
            && ((eligibleVehicles.length >= driversNotStudent.length && everyDriverAssigned(driversNotStudent, vehicleDrivers.value))
                || (eligibleVehicles.length < driversNotStudent.length && driversOnlyAssignedOnce(driversNotStudent, vehicleDrivers.value)));
    };

    const clearVehiclePrimaryDriverAssignments = () => {
        vehicleDrivers.value = [];
        const eligibleVehicles = getVehiclesForAssignment();
        eligibleVehicles.forEach((el) => {
            const vehicleDriver = viewModelService.create({}, 'pc', 'edge.capabilities.policyjob.lob.personalauto.coverables.dto.VehicleDriverDTO');
            vehicleDriver.vehicleID.value = el.fixedId;
            vehicleDriver.driverID.value = 0;
            vehicleDriver.percentageDriven_Ext.value = TOTAL_PCT;
            vehicleDrivers.pushElement(vehicleDriver.value);
        });
    };

    const removeAssignmentsForNonEligibleVehicles = () => {
        const eligibleVehicles = getVehiclesForAssignment();
        // remove VehicleDriver objects for non-eligible vehicles
        let idx = 0;

        while (idx < vehicleDrivers.value.length) {
            const vd = vehicleDrivers.value[idx];
            if (eligibleVehicles.findIndex((veh) => vd.vehicleID === veh.fixedId) === -1) {
                const index = vehicleDrivers.value.findIndex((obj) => obj === vd);
                vehicleDrivers.value.splice(index, 1);
            } else {
                idx += 1;
            }
        }

        vehicleDrivers.value.filter((vd) => {
            return eligibleVehicles.findIndex((veh) => vd.vehicleID === veh.fixedId) !== -1;
        });
    };

    const removeAssignmentsForNonEligibleDrivers = () => {
        vehicleDrivers.value = vehicleDrivers.value.reduce((acc, vd) => {
            if (vd.driverID) {
                if (drivers.findIndex((drv) => vd.driverID === drv.fixedId) === -1) {
                    if (vehicleDrivers.value.filter((vehdrv) => vehdrv.vehicleID === vd.vehicleID).length > 1) {
                        // this is percentage state so remove rows for driver
                        return acc;
                    }
                    // single row for each vehicle so remove driver
                    _.set(vd, 'driverID', null);
                }
            }
            acc.push(vd);
            return acc;
        }, []);
    };

    const buildVehicleDriverListForVehicle = (id) => {
        drivers.forEach((el) => {
            // eslint-disable-next-line react/prop-types
            const tmp = vehicleDrivers.value.find((vd) => {
                return vd.driverID === el.fixedId && vd.vehicleID === id;
            });

            if (!tmp) {
                const vdVM = viewModelService.create({}, 'pc', 'edge.capabilities.policyjob.lob.personalauto.coverables.dto.VehicleDriverDTO');
                vdVM.vehicleID.value = id;
                vdVM.driverID.value = el.fixedId;
                vdVM.percentageDriven_Ext.value = 0;
                vehicleDrivers.pushElement(vdVM.value);
            }
        });
    };

    const isRatedDriver = (id) => {
        return !!drivers.find((el) => {
            return el.fixedId === id;
        });
    };

    const createNewVehicleDriverAssignment = (vehicleID, driverID, percentage) => {
        const vdVM = viewModelService.create({}, 'pc', 'edge.capabilities.policyjob.lob.personalauto.coverables.dto.VehicleDriverDTO');
        vdVM.vehicleID.value = vehicleID;
        vdVM.driverID.value = driverID;
        vdVM.percentageDriven_Ext.value = percentage;
        vehicleDrivers.pushElement(vdVM.value);
        return vdVM;
    };

    const getVehicleDriverForVehicle = (id) => {
        let assignment = null;
        const vehDriver = vehicleDrivers.value.find((vd) => vd.vehicleID === id);
        if (vehDriver) {
            assignment = vehicleDrivers.findFirstElement(vehDriver);
        } else {
            assignment = createNewVehicleDriverAssignment(id, null, 100);
        }
        return assignment;
    };

    const getVehicleDriversForVehicle = (id) => {
        return vehicleDrivers.filter((vd) => vd.vehicleID?.value === id && isRatedDriver(vd.driverID?.value));
    };

    const isTotalPctError = (pct, id) => {
        if (pct === TOTAL_PCT) {
            if (errors.map((el) => el.id).includes(id)) setErrors((prevState) => prevState.filter((el) => el.id !== id));
        } else {
            setErrors((prevState) => [...prevState, { id: id, err: translator(messages.totalPercentageDesc) }]);
        }
        return pct !== TOTAL_PCT;
    };

    useEffect(() => {
        const TOTAL_PCT_ERROR = translator(messages.totalPercentageDesc);

        if (errors.length > 0) {
            if (!vehicleDriverErrors.includes(TOTAL_PCT_ERROR)) setVehicleDriverErrors((prevState) => [...prevState, TOTAL_PCT_ERROR]);
        } else {
            setVehicleDriverErrors((prevState) => prevState.filter((el) => el !== TOTAL_PCT_ERROR));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [errors, translator]);

    const updateTotalPercentage = (id) => {
        const vehicle = vehicles.find((veh) => veh.fixedId === id);
        if (!isPolicyChange || (isPolicyChange && isVehicleEligibleForAccidentPrevention(vehicle))) {
            const vehicleDriversForVehicle = getVehicleDriversForVehicle(id);
            vehicleDriversForVehicle.forEach((vd) => {
                vd.percentageDriven_Ext.value = !vd.percentageDriven_Ext.value ? 0 : parseInt(vd.percentageDriven_Ext.value, 10);
            });
            vehicle.totalPercentage = vehicleDriversForVehicle
                .map((val) => val.percentageDriven_Ext.value)
                .reduce((acc, cur) => {
                    const currentVal = Number.isNaN(parseInt(cur, 10)) ? 0 : parseInt(cur, 10);
                    return parseInt(acc, 10) + currentVal;
                }, 0);
            vehicle.totalPctError = isTotalPctError(vehicle.totalPercentage, vehicle.fixedId);
        }
    };

    const formatDriverOptions = () => {
        if (!drivers) return [];
        return drivers.map((driver) => ({
            code: driver.fixedId,
            name: translator({
                id: driver.getDisplayName(),
                defaultMessage: driver.getDisplayName()
            })
        }));
    };

    const isVehiclePrivateOrClassic = (vehicle) => {
        if (_.isNil(vehicle.type_ML)) {
            return false;
        }
        switch (vehicle.type_ML) {
            case 'private_passenger_autos_ml':
            case 'classic_ml':
                return true;
            default:
                return false;
        }
    };

    const getVehicleName = (vehicle) => {
        if (isVehiclePrivateOrClassic(vehicle)) {
            return `${vehicle.getYearMakeModelNameML()} ${vehicle.trim_ML}`;
        }
        return vehicle.getYearMakeModelNameML();
    };


    useEffect(() => {
        if (isVehiclePrimaryDriverState) {
            removeAssignmentsForNonEligibleDrivers();
            removeAssignmentsForNonEligibleVehicles();
            if (!isPolicyChange && !validateVehiclePrimaryDriverAssignments()) {
                clearVehiclePrimaryDriverAssignments();
            }
        } else if (isVehiclePrimaryDriverPercentageState) {
            removeAssignmentsForNonEligibleDrivers();
            removeAssignmentsForNonEligibleVehicles();
            const eligibleVehicles = getVehiclesForAssignment();
            eligibleVehicles.forEach((vehicle) => {
                buildVehicleDriverListForVehicle(vehicle.fixedId);
                updateTotalPercentage(vehicle.fixedId);
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const getVehiclesOverrides = () => {
        const overrides = _.orderBy(getVehiclesForAssignment(), ['vehicleNumber']).map((vehicle, index) => {
            return {
                [`vehicleInfo${index}`]: {
                    content: translator(messages.vehicleInfo, { vehicle: getVehicleName(vehicle).toUpperCase(), vehicleNumber: vehicle.vehicleNumber_ML })
                },
                [`primaryDriverStateVehicleContainer${index}`]: {
                    visible: isVehiclePrimaryDriverState
                },
                [`primaryDriverPercentageStateVehicleContainer${index}`]: {
                    visible: isVehiclePrimaryDriverPercentageState
                },
                [`driverDropdown${index}`]: {
                    availableValues: formatDriverOptions(),
                    value: isVehiclePrimaryDriverState ? getVehicleDriverForVehicle(vehicle.fixedId).driverID.value : undefined,
                    onValueChange: (value) => {
                        if (isVehiclePrimaryDriverState) {
                            getVehicleDriverForVehicle(vehicle.fixedId).driverID.value = Number(value);
                            refreshData();
                        }
                    }
                },
                [`driverDropdownTooltip${index}`]: {
                    tooltipLabel: messages.tooltipLabel,
                    tooltipContent: messages.driverTooltipContent
                },
                [`vehicleDriversComponent${index}`]: {
                    model: isVehiclePrimaryDriverPercentageState ? getVehicleDriversForVehicle(vehicle.fixedId) : undefined,
                    vehicleIndex: index,
                    drivers,
                    updateTotalPercentage,
                    refreshData
                },
                [`percentageDriverTooltip${index}`]: {
                    tooltipHeader: messages.tooltipLabel,
                    tooltipContent: messages.percentageDriverTooltipContent,
                    tooltipWrapperClassName: styles.inlineTooltip
                },
                [`totalPercentageValue${index}`]: {
                    content: `${vehicle.totalPercentage} %`
                },
                [`totalPercentageDescription${index}`]: {
                    className: vehicle.totalPctError ? 'error-help-text font-weight-bold' : ''
                }
            };
        });

        return Object.assign({}, ...overrides);
    };

    const getPolicyChangeVehiclesOverrides = () => {
        const overrides = _.orderBy(getVehiclesForAssignment(), ['vehicleNumber']).map((vehicle, index) => {
            return {
                [`vehicleHeaderContainer${index}`]: {
                    visible: !isGPA
                },
                [`vehicleXContainer${index}`]: {
                    content: translator(messages.vehicleX, { vehicleNumber: vehicle.vehicleNumber })
                },
                [`vehicleNameContainer${index}`]: {
                    content: getVehicleName(vehicle).toUpperCase()
                },
                [`vehicleHeaderGPAContainer${index}`]: {
                    visible: isGPA,
                    content: `${getVehicleName(vehicle).toUpperCase()}${translator(messages.vehicleXForGPA, { vehicleNumber: vehicle.vehicleNumber })}`
                },
                [`primaryDriverStateVehicleContainer${index}`]: {
                    visible: isVehiclePrimaryDriverState
                },
                [`driverDropdown${index}`]: {
                    availableValues: formatDriverOptions(),
                    value: isVehiclePrimaryDriverState ? getVehicleDriverForVehicle(vehicle.fixedId).driverID.value : undefined,
                    onValueChange: (value) => {
                        if (isVehiclePrimaryDriverState) {
                            getVehicleDriverForVehicle(vehicle.fixedId).driverID.value = Number(value);
                            refreshData();
                        }
                    },
                    tooltip: {
                        visible: isGPA,
                        title: translator(messages.tooltipLabel),
                        text: translator(messages.driverTooltipContent)
                    }
                },
                [`driverDropdownTooltipContainer${index}`]: {
                    visible: !isGPA
                },
                [`driverDropdownTooltip${index}`]: {
                    tooltipLabel: messages.tooltipLabel,
                    tooltipContent: messages.driverTooltipContent
                },
                [`primaryDriverPercentageStateVehicleContainer${index}`]: {
                    visible: isVehiclePrimaryDriverPercentageState
                },
                [`percentageDriverTooltip${index}`]: {
                    tooltipHeader: messages.tooltipLabel,
                    tooltipContent: messages.percentageDriverTooltipContent,
                    tooltipWrapperClassName: styles.inlineTooltip
                },
                [`vehicleDriversComponent${index}`]: {
                    model: isVehiclePrimaryDriverPercentageState ? getVehicleDriversForVehicle(vehicle.fixedId) : undefined,
                    vehicleIndex: index,
                    drivers,
                    updateTotalPercentage,
                    refreshData,
                    isPolicyChange
                },
                [`totalPercentageValue${index}`]: {
                    content: `${vehicle.totalPercentage} %`
                },
                [`totalPercentageDescription${index}`]: {
                    className: vehicle.totalPctError ? 'error-help-text' : ''
                }
            };
        });

        return Object.assign({}, ...overrides);
    };

    const overrides = {
        vehiclesIterable: {
            data: _.orderBy(getVehiclesForAssignment(), ['vehicleNumber'])
        },
        ...getVehiclesOverrides()
    };

    const policychangeOverrides = {
        vehiclesIterable: {
            data: _.orderBy(getVehiclesForAssignment(), ['vehicleNumber'])
        },
        ...getPolicyChangeVehiclesOverrides()
    };

    const resolvers = {
        resolveComponentMap: {
            VehicleDriversComponent
        }
    }

    return (
        <ViewModelForm
            uiProps={isPolicyChange ? policychangeMetadata.componentContent : metadata.componentContent}
            overrideProps={isPolicyChange ? policychangeOverrides : overrides}
            componentMap={resolvers.resolveComponentMap}
        />
    );
};

VehiclesListComponent.propTypes = {
    isVehiclePrimaryDriverState: PropTypes.bool.isRequired,
    isVehiclePrimaryDriverPercentageState: PropTypes.bool.isRequired,
    isPolicyChange: PropTypes.bool,
    drivers: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    vehicles: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    vehicleDrivers: PropTypes.shape({
        value: PropTypes.arrayOf(PropTypes.shape({})),
        filter: PropTypes.func,
        pushElement: PropTypes.func
    }).isRequired,
    vehicleDriverErrors: PropTypes.arrayOf(PropTypes.string).isRequired,
    setVehicleDriverErrors: PropTypes.func.isRequired
};

VehiclesListComponent.defaultProps = {
    isPolicyChange: false,
    isGPA: false
};

export default VehiclesListComponent;
